import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Button from '../../components/UI/Button/Button';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import { CONTACTS_PAGE, NEWS_PAGE } from '../../router/paths';
import styles from './ConstructionProgressPage.module.css';

type ProgressUpdate = {
  date: string;
  title: string;
  description: string;
};

type VideoEntry = {
  date: string;
  title: string;
  description: string;
  paragraphs: string[];
  sourceType: 'embed' | 'file' | 'placeholder';
  src?: string;
  poster?: string;
};

const updates: ProgressUpdate[] = [
  {
    date: '21.04.2026',
    title: 'Новый видеоотчет со стройплощадки',
    description: 'Публикуем свежий видеоотчет о ходе строительства ЖК «Статус» от 21 апреля 2026 года.',
  },
  {
    date: '18.03.2026',
    title: 'Новый видеоотчет со стройплощадки',
    description: 'Публикуем свежий видеоотчет о ходе строительства ЖК «Статус» от 18 марта 2026 года.',
  },
  {
    date: '13.03.2026',
    title: 'Заливка 9 этажа и кладка стен во 2 блоке',
    description: 'Во втором блоке продолжается заливка 9 этажа, и параллельно ведётся кладка стен из керамоблока.',
  },
  {
    date: '20.02.2026',
    title: 'Заливка 8 этажа 3 блока',
    description: 'Строительство жилого комплекса продолжается в стабильном темпе, а каждый завершенный этап подтверждает уверенное движение проекта вперед.',
  },
  {
    date: '12.12.2025',
    title: 'Доставка керамоблока и подготовка к кладке стен',
    description: 'На площадку доставлен керамоблок, и объект переходит к следующему важному этапу работ — кладке стен.',
  },
  {
    date: '02.06.2025',
    title: 'Заливка 4 этажа 4 блока',
    description: 'Работы на объекте идут последовательно, а дом с каждым днем поднимается все выше.',
  },
  {
    date: '28.03.2025',
    title: 'Заливка 3 этажа 2 блока',
    description: 'Строительство ЖК «Статус» продолжается по плану, и каждый новый этап становится еще одним уверенным шагом вперед.',
  },
  {
    date: '10.03.2025',
    title: 'Заливка 3 этажа 3 блока',
    description: 'Проект уверенно движется вперед, а каждый новый этап наглядно подтверждает стабильный ход строительства.',
  },
];

const publicVideoPath = (fileName: string) => `/videos/construction-progress/${fileName}`;
const publicPosterPath = (fileName: string) => `/videos/construction-progress/posters/${fileName}`;

const videos: VideoEntry[] = [
  {
    date: '21.04.2026',
    title: 'Видеоотчет о ходе строительства',
    description: '21.04.2026 опубликован новый видеоотчет со стройплощадки ЖК «Статус».',
    paragraphs: [
      'Публикуем свежее видео со строительной площадки, чтобы вы могли видеть актуальный ход работ без задержек и догадок.',
      'Следите за новыми обновлениями на странице хода строительства ЖК «Статус».',
    ],
    sourceType: 'file',
    src: publicVideoPath('21.04.2026.mp4'),
    poster: publicPosterPath('21.04.2026.png'),
  },
  {
    date: '18.03.2026',
    title: 'Видеоотчет о ходе строительства',
    description: '18.03.2026 опубликован новый видеоотчет со стройплощадки ЖК «Статус».',
    paragraphs: [
      'Продолжаем делиться актуальными материалами со стройки, чтобы вы могли видеть прогресс проекта в реальном времени.',
      'Следите за новыми обновлениями на странице хода строительства ЖК «Статус».',
    ],
    sourceType: 'file',
    src: publicVideoPath('18.03.2026.mp4'),
    poster: publicPosterPath('18.03.2026.png'),
  },
  {
    date: '13.03.2026',
    title: 'Заливка 9 этажа и кладка стен во 2 блоке',
    description: '13.03.2026 во втором блоке продолжается заливка 9 этажа, и параллельно ведётся кладка стен из керамоблока.',
    paragraphs: [
      'Стройка идёт активно: на площадке работают бригады строителей и техника, выполняются сразу несколько этапов работ.',
      'С каждым новым этажом наш дом становится всё выше, а будущие квартиры — всё ближе к своим владельцам.',
      'Мы продолжаем двигаться по графику и регулярно делимся с вами новостями со стройки.',
      'Следите за ходом строительства ЖК «Статус».',
      'Строительная компания «Восток Строй»',
    ],
    sourceType: 'file',
    src: publicVideoPath('13.03.2026.mp4'),
    poster: publicPosterPath('13.03.2026.png'),
  },
  {
    date: '10.03.2025',
    title: 'Заливка 3 этажа 3 блока',
    description: '10 марта 2025 года на объекте выполнена заливка 3 этажа третьего блока.',
    paragraphs: [
      'Строительство продолжается в активном темпе, а это значит, что проект уверенно движется вперед.',
      'Для наших клиентов это не просто очередной этап работ — это наглядное подтверждение того, что дом растет, а каждый этап приближает момент завершения строительства.',
      'Следите за новостями ЖК «Статус» 🏗️',
    ],
    sourceType: 'file',
    src: publicVideoPath('10.03.2025.mp4'),
    poster: publicPosterPath('10.03.2025.png'),
  },
  {
    date: '28.03.2025',
    title: 'Заливка 3 этажа 2 блока',
    description: '28.03.2025 на объекте выполнена заливка 3 этажа второго блока.',
    paragraphs: [
      'Строительство ЖК «Статус» продолжается по плану, и каждый новый этап — это еще один уверенный шаг вперед.',
      'Дом растет, работы активно ведутся, а проект постепенно приобретает свои реальные очертания.',
      'Для наших клиентов это важный показатель стабильного хода строительства и наглядное подтверждение того, что работа на объекте не останавливается.',
      'Следите за новостями строительства ЖК «Статус» 🏗️',
    ],
    sourceType: 'file',
    src: publicVideoPath('28.03.2025.mp4'),
    poster: publicPosterPath('28.03.2025.png'),
  },
  {
    date: '02.06.2025',
    title: 'Заливка 4 этажа 4 блока',
    description: '02.06.2025 на объекте выполнена заливка 4 этажа 4 блока.',
    paragraphs: [
      'Строительство ЖК «Статус» продолжается в хорошем темпе, и каждый завершённый этап приближает проект к реализации.',
      'Работы на объекте идут последовательно, а дом с каждым днём поднимается всё выше.',
      'Для будущих жильцов это не просто строительный процесс, а наглядное подтверждение того, что проект уверенно развивается и движется вперёд.',
      'Следите за новостями строительства ЖК «Статус» 🏗️',
    ],
    sourceType: 'file',
    src: publicVideoPath('02.06.2025.mp4'),
    poster: publicPosterPath('02.06.2025.png'),
  },
  {
    date: '12.12.2025',
    title: 'Доставка керамоблока и подготовка к кладке стен',
    description:
      '12.12.2025 на строительную площадку ЖК «Статус» был доставлен керамоблок — сейчас объект готовится к следующему важному этапу работ, кладке стен.',
    paragraphs: [
      'Строительство жилого комплекса продолжается последовательно и поэтапно. Поставка керамоблока на объект — это важный знак того, что работы переходят к следующей стадии, а значит, проект уверенно движется вперед.',
      'Подготовка к кладке — это не просто технический момент, а важная часть строительного процесса, которая говорит о развитии объекта и приближении к следующим результатам.',
      'На площадке продолжается активная работа, и с каждым новым этапом ЖК «Статус» всё больше приобретает очертания будущего современного жилого комплекса.',
      'Мы и дальше будем делиться с вами актуальными новостями со стройки, чтобы вы могли следить за реальным ходом строительства, видеть прогресс своими глазами и быть в курсе всех важных этапов.',
      'Следите за новостями строительства ЖК «Статус» 🏗️',
    ],
    sourceType: 'file',
    src: publicVideoPath('12.12.2025.mp4'),
    poster: publicPosterPath('12.12.2025.png'),
  },
  {
    date: '20.02.2026',
    title: 'Заливка 8 этажа 3 блока',
    description: '20.02.2026 на объекте успешно выполнена заливка 8 этажа 3 блока.',
    paragraphs: [
      'Строительство жилого комплекса «Статус» продолжается в стабильном и уверенном темпе. Каждый новый завершенный этап — это не просто строительный процесс, а важный результат большой ежедневной работы всей команды на объекте.',
      'Дом постепенно поднимается всё выше, а проект шаг за шагом приближается к своей полной реализации.',
      'Заливка 8 этажа 3 блока — это очередное подтверждение того, что работы ведутся активно, последовательно и по намеченному плану.',
      'Для будущих жильцов это особенно важная новость, потому что с каждым новым этапом становится всё заметнее реальный прогресс строительства и всё ближе момент, когда дом будет готов встречать своих жителей.',
      'Мы продолжаем делиться с вами актуальными новостями со стройплощадки, чтобы вы могли видеть, как продвигается строительство ЖК «Статус», как растёт дом и как день за днём реализуется проект современного жилого комплекса.',
      'Следите за нашими обновлениями — впереди ещё больше новостей о ходе строительства ЖК «Статус» 🏗️',
    ],
    sourceType: 'file',
    src: publicVideoPath('20.02.2026.mp4'),
    poster: publicPosterPath('20.02.2026.png'),
  },
];

const parseDate = (value: string): number => {
  const [day, month, year] = value.split('.').map(Number);
  if (!day || !month || !year) return 0;
  return new Date(year, month - 1, day).getTime();
};

export default function ConstructionProgressPage() {
  const sortedVideos = [...videos].sort((a, b) => parseDate(b.date) - parseDate(a.date));
  const sortedUpdates = [...updates].sort((a, b) => parseDate(b.date) - parseDate(a.date));

  return (
    <>
      <Helmet>
        <title>Ход строительства ЖК «Статус» | Восток Строй</title>
        <meta
          name='description'
          content='Видеоотчеты, фотографии и свежие текстовые обновления со стройплощадки ЖК «Статус».'
        />
        <meta name='robots' content='index, follow' />
        <link rel='canonical' href='https://vostokstroy.com/projects/progress' />
      </Helmet>

      <Header theme={HeaderType.Light} />

      <main className={styles.content}>
        <section className={styles.hero}>
          <p className={styles.badge}>Ход строительства</p>
          <h1>Свежие новости со стройки ЖК «Статус»</h1>
          <p className={styles.hero_description}>
            На странице собраны видеоотчеты, фотографии со стройплощадки и подробные текстовые обновления по ключевым
            этапам строительства.
          </p>
          <div className={styles.hero_actions}>
            <Link to={CONTACTS_PAGE}>
              <Button roundType='small_rounded' paddingType='big'>
                Связаться с менеджером
              </Button>
            </Link>
            <Link className={styles.secondary_link} to={NEWS_PAGE}>
              Перейти в новости
            </Link>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Видеоотчеты по датам</h2>
          <div className={styles.video_timeline}>
            {sortedVideos.map((item) => (
              <article className={styles.video_timeline_item} key={item.date + item.title}>
                <p className={styles.video_timeline_date}>{item.date}</p>
                <div className={styles.video_timeline_content}>
                  {item.sourceType === 'embed' && item.src && (
                    <div className={styles.video_frame_wrapper}>
                      <iframe
                        src={item.src}
                        width='100%'
                        height={420}
                        style={{ border: 0 }}
                        allow='autoplay; encrypted-media; fullscreen; picture-in-picture;'
                        allowFullScreen
                        title={`${item.title}-${item.date}`}
                      />
                    </div>
                  )}

                  {item.sourceType === 'file' && item.src && (
                    <div className={styles.video_file_wrapper}>
                      <video className={styles.video_player} controls preload='metadata' playsInline poster={item.poster}>
                        <source src={item.src} type='video/mp4' />
                        Ваш браузер не поддерживает видео.
                      </video>
                    </div>
                  )}

                  {item.sourceType === 'placeholder' && (
                    <div className={styles.video_placeholder}>
                      <p>Видеоотчет за {item.date} будет опубликован в ближайшее время</p>
                    </div>
                  )}

                  <div className={styles.video_text_block}>
                    <span className={styles.video_badge}>ЖК «Статус»</span>
                    <div className={styles.video_copy}>
                      <h4>{item.title}</h4>
                      <p className={styles.video_description}>{item.description}</p>
                    </div>
                    <div className={styles.video_story}>
                      {item.paragraphs.map((paragraph) => (
                        <p className={styles.video_paragraph} key={paragraph}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
          <p className={styles.note}>Все видео расположены по дате — от самых свежих новостей к более ранним этапам.</p>
        </section>

        <section className={styles.section}>
          <h2>Ключевые этапы работ</h2>
          <div className={styles.timeline}>
            {sortedUpdates.map((item) => (
              <article className={styles.timeline_item} key={item.date + item.title}>
                <p className={styles.timeline_date}>{item.date}</p>
                <div className={styles.timeline_content}>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer theme={FooterType.Light} />
    </>
  );
}
